import { useCallback, useEffect, useMemo, useRef } from 'react';

import { env } from '@/env';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setActiveGames, setSelectedGames } from '@/store/core.slice';
import { useGamesQuery } from '@/services/endpoints/games';
import {
  BACKGAMMON_GAME,
  CHESS_GAME,
  CHINESE_POKER_GAME,
  COC_GAME,
  CR_GAME,
  CR_GAME_ID,
} from '@/lib/gameFeatures';
import { storage, StorageKey } from '@/lib/storage';
import type { Game } from '@/types';
import { laravelOwnsData } from '@/lib/dataOwner';

/**
 * Game-selection state manager. Mirrors mobile-app's single-game picker
 * (`selectedGames === activeGames === [id]`) and persists to localStorage so
 * the choice survives reloads.
 *
 * Rehydrates from storage on first mount, then keeps storage in sync as the
 * user picks a different game.
 */
export function useGameSelection() {
  const dispatch = useAppDispatch();
  const selectedGames = useAppSelector((s) => s.core.selectedGames);
  const activeGames = useAppSelector((s) => s.core.activeGames);
  // ⚠️ `/games/available` e' una rotta del MONOLITE, e su una build Limulo non e' solo
  // inutile: e' l'ultima cosa che teneva vivo l'exchange-token. Trovata da or45 con uno
  // stack-probe iniettato nel runner, non per ispezione — e non si poteva trovare
  // leggendo: la query si chiama `useGamesQuery`, non ha «Laravel» nel nome, e vive due
  // livelli sotto `HubSections`, che di API Laravel non ne importa nessuna.
  // 📌 *La grep sui nomi che sembrano Laravel non lo trova; lo stack si'.*
  //
  // 🔑 La perdita e' NULLA, misurata e non stimata: il fallback qui sotto garantisce
  // `activeGame` anche quando la query fallisce — ed e' esattamente cio' che succedeva
  // gia', perche' col monolite a 503 `backendGames` era gia' vuoto. Spegnerla non cambia
  // una schermata: toglie ~9 secondi di attesa per un elenco che arrivava vuoto.
  //
  // ⚠️ E quando il monolite tornera' su, su una build Limulo quell'elenco resterebbe
  // comunque il VOCABOLARIO SBAGLIATO: i tornei Limulo portano un `gameId` che e' un
  // codice, non l'`id` numerico di Laravel (kb#81). Il selettore giusto per una build
  // Limulo si costruisce su `/v1/games` — e' un lavoro di prodotto, non uno skip.
  const { data: backendGames = [], isLoading } = useGamesQuery(undefined, {
    skip: !laravelOwnsData(),
  });
  // QChess / QClans / QGammon aren't Laravel games — inject synthetic "Chess" /
  // "Clash of Clans" / "Backgammon" entries into the picker (and into activeGame
  // resolution) when their feature flags are on, so they're selectable like
  // Clash Royale / eFootball.
  const games = useMemo(() => {
    const extra: Game[] = [];
    // Clash Royale fallback: guarantees `activeGame` resolves for the default
    // game even before /games/available loads (or when it fails). The real
    // backend row (same id) wins because `find` scans backendGames first.
    if (!backendGames.some((g) => g.id === CR_GAME_ID)) extra.push(CR_GAME);
    if (env.featureChessEnabled) extra.push(CHESS_GAME);
    if (env.featureCocEnabled) extra.push(COC_GAME);
    if (env.featureBackgammonEnabled) extra.push(BACKGAMMON_GAME);
    if (env.featureChinesePokerEnabled) extra.push(CHINESE_POKER_GAME);
    return extra.length > 0 ? [...backendGames, ...extra] : backendGames;
  }, [backendGames]);

  const rehydrated = useRef(false);
  useEffect(() => {
    if (rehydrated.current) return;
    rehydrated.current = true;
    if (env.featureCrOnly) {
      // Single-game build: the active game is Clash Royale, period — also
      // overwrites a stale non-CR selection left over from a multi-game
      // build, which would otherwise be unfixable with the picker hidden.
      dispatch(setSelectedGames([CR_GAME_ID]));
      dispatch(setActiveGames([CR_GAME_ID]));
      storage.set(StorageKey.SelectedGameId, String(CR_GAME_ID));
      return;
    }
    const stored = storage.get(StorageKey.SelectedGameId);
    if (!stored) {
      // First run (or cleared storage): default to Clash Royale. The public
      // launch is CR-exclusive and a null activeGame renders a broken Hub
      // (no CR sections, unfiltered Rankings).
      dispatch(setSelectedGames([CR_GAME_ID]));
      dispatch(setActiveGames([CR_GAME_ID]));
      storage.set(StorageKey.SelectedGameId, String(CR_GAME_ID));
      return;
    }
    const id = Number(stored);
    if (!Number.isFinite(id) || id <= 0) return;
    dispatch(setSelectedGames([id]));
    dispatch(setActiveGames([id]));
  }, [dispatch]);

  const activeGameId = activeGames[0] ?? null;
  const activeGame: Game | null = useMemo(() => {
    if (!activeGameId) return null;
    return games.find((g) => g.id === activeGameId) ?? null;
  }, [activeGameId, games]);

  const selectGame = useCallback(
    (id: number) => {
      dispatch(setSelectedGames([id]));
      dispatch(setActiveGames([id]));
      storage.set(StorageKey.SelectedGameId, String(id));
    },
    [dispatch],
  );

  const clearGame = useCallback(() => {
    dispatch(setSelectedGames([]));
    dispatch(setActiveGames([]));
    storage.remove(StorageKey.SelectedGameId);
  }, [dispatch]);

  return {
    games,
    isLoading,
    activeGame,
    activeGameId,
    selectedGames,
    activeGames,
    selectGame,
    clearGame,
  };
}
